<template>
    <div class="oauth-callback">
        <div v-if="loading" class="loading-state">
            <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
            <h3>Processing authentication...</h3>
            <p>Please wait while we complete your integration setup.</p>
        </div>
        <div v-else-if="error" class="error-state">
            <i
                class="pi pi-exclamation-circle"
                style="font-size: 2rem; color: var(--red-500)"
            ></i>
            <h3>Authentication Error</h3>
            <p>{{ error }}</p>
            <Button
                label="Back to Integrations"
                icon="pi pi-arrow-left"
                @click="goToIntegrations"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";
import { useServices } from "@/lib/openapi";

const route = useRoute();
const router = useRouter();
const toast = useToast();

const loading = ref(true);
const error = ref<string | null>(null);

const stekId = ref("");
const orgId = ref("");

async function exchangeCodeForToken() {
    try {
        // Extract code and state from URL parameters
        const code = route.query.code as string;
        const state = route.query.state as string;

        if (!code) {
            throw new Error("No authorization code received");
        }

        const [integrationType, stateOrgId, stateStekId] = state.split(":");

        // Get the redirect URI (current URL)
        const redirectUri = window.location.href.split("?")[0];

        // Call the backend to exchange the code for a token
        const api = useServices({
            requires: {
                auth: true,
                organization: true,
                stek: true,
            },
            overrides: {
                organizationId: stateOrgId,
                stekId: stateStekId,
            },
        });
        const { response, data } =
            await api.exchangeTokenIntegrationsOauth2ClientTokenPost({
                body: {
                    code,
                    state,
                    redirect_uri: redirectUri,
                    integration_type: integrationType,
                },
            });

        if (!response.ok || !data) {
            const errorData = await response.json();
            throw new Error(
                errorData.detail || "Failed to exchange code for token"
            );
        }

        stekId.value = stateStekId;
        orgId.value = stateOrgId;

        // Show success message
        toast.add({
            severity: "success",
            summary: "Integration Connected",
            detail: "ClickUp integration was successfully configured",
            life: 5000,
        });

        // Redirect to integrations page
        goToIntegrations();
    } catch (err) {
        console.error("Error in OAuth callback:", err);
        error.value =
            err instanceof Error ? err.message : "An unknown error occurred";
        loading.value = false;
    }
}

function goToIntegrations() {
    if (orgId.value && stekId.value) {
        router.push({
            name: "integrationManagement",
            params: {
                orgName: orgId.value,
                stekName: stekId.value,
            },
        });
    } else {
        router.push("/");
    }
}

onMounted(() => {
    exchangeCodeForToken();
});
</script>

<style scoped lang="scss">
.oauth-callback {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    text-align: center;
    padding: 2rem;

    .loading-state,
    .error-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;

        h3 {
            margin: 0;
        }

        p {
            margin: 0;
            color: var(--text-color-secondary);
        }
    }

    .error-state {
        button {
            margin-top: 1rem;
        }
    }
}
</style>
